import { saveAs } from "file-saver"
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeadingLevel,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TextRun,
  convertInchesToTwip,
} from "docx"
import {
  FONT,
  OIO_PAGE as OIA_PAGE,
  SIZES,
  SPACING,
  createFormattedRuns,
  elementToDocx,
  oioDocumentStyles as oiaDocumentStyles,
  oioLegalNumbering as oiaLegalNumbering,
  parseMarkdownContent,
} from "../../oio/lib/oio-formatter"
import {
  OIA_NO_PLACEHOLDER,
  canonicalIds,
  fullTitles,
  planNumbering,
  sectionHeadings,
  stripTypedNumber,
  type PlannedBlock,
  type SectionHeading,
  type SectionTitles,
} from "../sections"

/** Exact header/footer copy for simulator drafts (global constraints). */
export const SIMULATED_BANNER = "SIMULATED DRAFT — NOT AN ORDER"

export interface OiaSimSection {
  id: string
  markdown: string
}

export interface OiaSimDocMeta {
  title: string
  /** Ground points from GET /draft, for the "(i) Whether ..." sub-heads. */
  titles?: SectionTitles
  /** Appellant for the running header; defaults to the title. */
  party?: string
}

type Block = Paragraph | Table

const plain = (text: string, opts: { align?: (typeof AlignmentType)[keyof typeof AlignmentType]; bold?: boolean; after?: number } = {}) =>
  new Paragraph({
    children: [new TextRun({ text, font: FONT, size: SIZES.BODY, bold: opts.bold })],
    alignment: opts.align ?? AlignmentType.LEFT,
    spacing: { before: SPACING.PARA_BEFORE, after: opts.after ?? SPACING.PARA_AFTER, line: SPACING.LINE },
  })

/** D1: the caption section as centred lines (office, appeal particulars, ORDER-IN-APPEAL, Dated, DIN, Passed by); the first line and the ORDER-IN-APPEAL / Passed by lines are bold. */
function captionBlock(b: PlannedBlock): Block[] {
  return b.lines.map((raw, i) => {
    const line = raw.replace(/\*+/g, "").trim()
    return plain(line, { align: AlignmentType.CENTER, bold: i === 0 || /^ORDER-IN-APPEAL\b|^Passed by\b/i.test(line) })
  })
}

/** D2: real Word heading styles; PREAMBLE and ORDER are centred and underlined. */
function headingBlock(h: { text: string; level: 1 | 2; center?: boolean }): Paragraph {
  return new Paragraph({
    heading: h.level === 1 ? HeadingLevel.HEADING_1 : HeadingLevel.HEADING_2,
    children: createFormattedRuns(h.text, {
      bold: true,
      size: h.level === 1 ? SIZES.HEADING : SIZES.BODY,
      allCaps: h.level === 1,
      underline: h.center ? { type: "single" } : undefined,
    }),
    alignment: h.center ? AlignmentType.CENTER : AlignmentType.LEFT,
    spacing: { before: SPACING.HEADING_BEFORE, after: SPACING.HEADING_AFTER, line: SPACING.LINE },
  })
}

const asDocx = (md: string): Block[] => parseMarkdownContent(md, { explicitHeadingsOnly: true }).flatMap(elementToDocx)

const continuation = (line: string): Paragraph =>
  new Paragraph({
    children: createFormattedRuns(line, { size: SIZES.BODY }),
    alignment: AlignmentType.JUSTIFIED,
    spacing: { before: 0, after: SPACING.PARA_AFTER, line: SPACING.LINE },
    indent: { left: convertInchesToTwip(0.75) },
  })

/** One planned markdown block as Word paragraphs. */
function blockToDocx(id: string, b: PlannedBlock): Block[] {
  if (id === "caption" && b.kind === "para") return captionBlock(b)
  switch (b.kind) {
    case "heading":
    case "table":
      return asDocx(b.lines.join("\n"))
    case "quote": {
      // D6: quoted statute extracts are indented, justified and in quotation marks.
      const text = b.lines.join(" ").trim()
      const quoted = /^["“]/.test(text) ? text : `“${text}”`
      return [
        new Paragraph({
          children: createFormattedRuns(quoted, { size: SIZES.BODY }),
          alignment: AlignmentType.JUSTIFIED,
          spacing: { before: SPACING.PARA_BEFORE, after: SPACING.PARA_AFTER, line: SPACING.LINE },
          indent: { left: convertInchesToTwip(0.5), right: convertInchesToTwip(0.5) },
        }),
      ]
    }
    case "list":
      return b.lines.flatMap((item, i) => {
        const [first, ...rest] = item.split("\n")
        const label = b.labels[i] ?? `${i + 1}`
        const numbered = elementToDocx({
          type: "numbered",
          number: label,
          content: b.labels[i] ? stripTypedNumber(first) : first,
          level: label.includes(".") ? 1 : 0,
        })
        // A Hindi/English pair is one item: the second line sits directly under the first, without a number.
        return [...numbered, ...rest.map(continuation)]
      })
    case "para": {
      const text = b.lines.join(" ")
      if (b.labels[0]) {
        return elementToDocx({
          type: "numbered",
          number: b.labels[0],
          content: stripTypedNumber(text),
          level: b.labels[0].includes(".") ? 1 : 0,
        })
      }
      if (b.tail) {
        // D5: the To block, copy-to list and signature lines are left-aligned, line by line.
        return b.lines.flatMap((line) =>
          /^\[.*\]$|^\(.*\)$/.test(line)
            ? elementToDocx({ type: "signature", content: line })
            : [
                new Paragraph({
                  children: createFormattedRuns(line, { size: SIZES.BODY }),
                  alignment: AlignmentType.LEFT,
                  spacing: { before: 20, after: 20, line: SPACING.LINE },
                }),
              ],
        )
      }
      return b.lines.flatMap(asDocx)
    }
  }
}

/**
 * Lays out the simulator's ordered markdown sections as one OIA-styled
 * document. Styling, markdown parsing and table rendering come from
 * `features/oio/lib/oio-formatter.ts`; the caption (from the `caption` section), headings, numbering, header and footer are simulator-specific.
 */
export function buildOiaSimDocx(sections: OiaSimSection[], meta: OiaSimDocMeta): Document {
  // Section markdown is body only: canonical headings, the caption and one continuous paragraph counter are added here, as on the page.
  const order = canonicalIds(sections.map((s) => s.id))
  const byId = new Map(sections.map((s) => [s.id, s.markdown]))
  const titles = fullTitles(meta.titles, byId.get("points"))
  const headings = sectionHeadings(order, titles, (id) => !!byId.get(id)?.trim())
  const plan = planNumbering(order.map((id) => ({ id, markdown: byId.get(id) ?? "" })))
  let lastGroup: string | undefined
  const body = order.flatMap((id) => {
    const planned = plan.get(id) ?? []
    if (!planned.length) return []
    const h: SectionHeading = headings[id]
    const out: Block[] = []
    if (h.group && h.group !== lastGroup) out.push(headingBlock({ text: h.group, level: 1 }))
    lastGroup = h.group ?? lastGroup
    if (h.text) out.push(headingBlock(h))
    out.push(...planned.flatMap((b) => blockToDocx(id, b)))
    return out
  })
  const children = body
  const party = (meta.party ?? meta.title).trim()

  return new Document({
    title: meta.title,
    styles: oiaDocumentStyles(),
    numbering: oiaLegalNumbering(),
    sections: [
      {
        properties: { page: OIA_PAGE },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: SIMULATED_BANNER,
                    font: FONT,
                    size: SIZES.FOOTER,
                    bold: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
                border: {
                  bottom: { color: "000000", space: 1, style: BorderStyle.SINGLE, size: 8 },
                },
              }),
              // D4: the running line of the actual order.
              new Paragraph({
                children: [new TextRun({ text: `OIA No. ${OIA_NO_PLACEHOLDER} / ${party}`, font: FONT, size: SIZES.FOOTER })],
                alignment: AlignmentType.RIGHT,
                spacing: { after: 120 },
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: SIMULATED_BANNER, font: FONT, size: SIZES.FOOTER, bold: true }),
                ],
                alignment: AlignmentType.CENTER,
                border: {
                  top: { color: "CCCCCC", space: 1, style: BorderStyle.SINGLE, size: 4 },
                },
                spacing: { before: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Page ", font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ children: [PageNumber.CURRENT], font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ text: " of ", font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], font: FONT, size: SIZES.FOOTER }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
        children,
      },
    ],
  })
}

/** Replaces characters that are invalid in filenames on common platforms. */
export function safeFilenamePart(title: string): string {
  return title.replace(/[\/\\:*?"<>|]/g, "-")
}

/** Builds the draft and saves it as `<title>-simulated-draft.docx`. */
export async function downloadOiaSimDocx(sections: OiaSimSection[], meta: OiaSimDocMeta): Promise<void> {
  const blob = await Packer.toBlob(buildOiaSimDocx(sections, meta))
  saveAs(blob, `${safeFilenamePart(meta.title)}-simulated-draft.docx`)
}
